import { useTranslation } from "translation/useTranslation";
import PayGradeSelect from "components/EmployeeManagement/inputs/PayGradeSelect";
import DepartmentSelect from "components/EmployeeManagement/inputs/DepartmentSelect";
import BulkFieldInput from "./BulkFieldInput";
import ListPagination from "components/ui/ListPagination";
import { Field, Input, InputAffix, NativeSelect, Select } from "components/ui";
import { StateInstanceForm } from "./DB-FillFields";

// Editing a companion field ticks its parent field's checkbox.
const COMPANION_PARENT = {
  pay_grade_level: 'pay_grade',
  uses_individual_pay_factor: 'individual_pay_factor',
  manual_average_value: 'trend',
  exclude_from_payout_reason: 'exclude_from_payout',
};

// Only checked fields are sent on apply; a checked-but-empty field clears that field.
export default function EmployeeInstanceEditor({
  employees,
  personaldatenFields,
  abrechnungsdatenFields,
  freeFields,
  groups,
  checked,
  setChecked,
  data,
  setData,
  fieldChecked,
  setFieldChecked,
  isDbModuleActive,
  stateExtraFields = [],
  stateSections,
  stateChecked,
  setStateChecked,
  stateValues,
  setStateValues,
  softwareSettings,
  fieldCustomNames,
  activePayfactorValues,
  onSelectionChange,
  search,
  onSearchChange,
  total,
  loadingPool,
  page,
  perPage,
  totalPages,
  totalRows,
  setPage,
  setPerPage,
}) {
  const { translate, editableTranslate } = useTranslation();

  const fieldLabel = (field) => {
    if (field.key === 'exclude_from_payout_reason') {
      return fieldCustomNames?.['field.exclude_from_payout'] || editableTranslate('field.exclude_from_payout', 'Ausschluss (Grenzwert)');
    }
    return fieldCustomNames?.[field?.translation_key] || editableTranslate(`field.${field.key.replace('_hash_id', '')}`, field.label);
  };

  const toggleRow = (hashId, isChecked) => {
    setChecked(prev => {
      if (isChecked) return prev.includes(hashId) ? prev : [...prev, hashId];
      return prev.filter(id => id !== hashId);
    });
    // Pass the employee up so its name/number survive pagination for the preview.
    if (onSelectionChange) {
      const employee = employees.find(e => e.hash_id === hashId) || { hash_id: hashId };
      onSelectionChange([employee], isChecked);
    }
  };

  const markChecked = (hashId, key) => {
    const parent = COMPANION_PARENT[key] || key;
    setFieldChecked(prev => {
      const cur = prev[hashId] || {};
      if (cur[parent]) return prev;
      return { ...prev, [hashId]: { ...cur, [parent]: true } };
    });
  };

  const toggleFieldChecked = (hashId, key, isChecked) => {
    setFieldChecked(prev => {
      const cur = { ...(prev[hashId] || {}) };
      if (isChecked) cur[key] = true; else delete cur[key];
      return { ...prev, [hashId]: cur };
    });
  };

  const setFieldValue = (hashId, key, value) => {
    setData(prev => ({ ...prev, [hashId]: { ...(prev[hashId] || {}), [key]: value } }));
    markChecked(hashId, key);
  };

  const renderInstanceField = (field, values, hashId) => {
    if (field.key === 'pay_grade') {
      return <PayGradeSelect
        activePayfactorValues={activePayfactorValues}
        grade={values['pay_grade']}
        level={values['pay_grade_level']}
        onGradeChange={(v) => setFieldValue(hashId, 'pay_grade', v)}
        onLevelChange={(v) => setFieldValue(hashId, 'pay_grade_level', v)}
      />;
    }
    if (field.key === 'department') {
      return <DepartmentSelect value={values['department']} onChange={(v) => setFieldValue(hashId, 'department', v)} />;
    }
    if (field.key === 'employee_group') {
      const groupOptions = (groups || []).map(g => ({ value: g.hash_id, label: g.name }));
      return <div style={{ display: 'contents' }} data-testid="instance-value-select">
        <Select
          options={groupOptions}
          value={groupOptions.find(o => o.value === values['employee_group']) || null}
          onChange={(option) => setFieldValue(hashId, 'employee_group', option ? option.value : '')}
          isClearable
          placeholder={translate('form.select_placeholder')}
        />
      </div>;
    }
    if (field.key === 'individual_pay_factor') {
      const enabled = !!values['uses_individual_pay_factor'];
      return <InputAffix
        prefix={<input type="checkbox" className="mr-2" checked={enabled} onChange={(e) => setFieldValue(hashId, 'uses_individual_pay_factor', e.target.checked)} data-testid={`instance-uses-individual-pay-factor-${hashId}`} />}
        readOnly={!enabled}
      >
        <Input data-testid={`instance-individual-pay-factor-${hashId}`} type="text" value={values['individual_pay_factor'] ?? ''} onChange={(e) => setFieldValue(hashId, 'individual_pay_factor', e.target.value)} readOnly={!enabled} maxLength={11} />
      </InputAffix>;
    }
    if (field.key === 'exclude_from_payout') {
      const excluded = values['exclude_from_payout'] === 'true' || values['exclude_from_payout'] === true;
      return <div className="d-flex select-with-input">
        <NativeSelect data-testid={`instance-exclude-from-payout-${hashId}`} className="w-auto" value={excluded ? 'true' : 'false'} onChange={(e) => setFieldValue(hashId, 'exclude_from_payout', e.target.value)}>
          <option value="false">{translate('form.no', 'nein')}</option>
          <option value="true">{translate('form.yes', 'Ja')}</option>
        </NativeSelect>
        <Input data-testid={`instance-exclude-from-payout-reason-${hashId}`} type="text" value={values['exclude_from_payout_reason'] ?? ''} onChange={(e) => setFieldValue(hashId, 'exclude_from_payout_reason', e.target.value)} readOnly={!excluded} maxLength={50} />
      </div>;
    }
    if (field.key === 'trend') {
      const trend = values['trend'] || '';
      return <>
        {['no', 'yes_calculated_from_budget', 'yes_manual_value'].map(opt => (
          <div className="form-check" key={opt}>
            <input type="radio" className="form-check-input" name={`instance-trend-${hashId}`} value={opt} checked={trend === opt} onChange={() => setFieldValue(hashId, 'trend', opt)} data-testid={`instance-trend-${hashId}-${opt}`} />
            <label className="form-check-label ml-2">{editableTranslate(`field.trend.${opt}`, opt)}</label>
          </div>
        ))}
        {trend === 'yes_manual_value' && (
          <div className="form-group mt-2 mb-0">
            <Field label={editableTranslate('field.trend.yes_manual_value.value', 'Wert')}>
              <Input data-testid={`instance-manual-average-value-${hashId}`} type="text" value={values['manual_average_value'] ?? ''} onChange={(e) => setFieldValue(hashId, 'manual_average_value', e.target.value)} maxLength={24} />
            </Field>
          </div>
        )}
      </>;
    }
    return <BulkFieldInput
      field={field}
      value={values[field.key]}
      onChange={(v) => setFieldValue(hashId, field.key, v)}
      excludeValue={values['exclude_from_payout']}
      onExcludeChange={(v) => setFieldValue(hashId, 'exclude_from_payout', v)}
      softwareSettings={softwareSettings}
      translate={translate}
      editableTranslate={editableTranslate}
    />;
  };

  const renderEmployeeForm = (employee) => {
    const values = data[employee.hash_id] || {};

    const renderFieldCell = (field, i) => {
      const cbId = `inst-${employee.hash_id}-${field.key}`;
      return (
        <div key={i} className="col-xl-4 col-lg-6 form-group">
          <div className="form-check mb-1">
            <input
              type="checkbox"
              id={cbId}
              data-testid={cbId}
              className="form-check-input"
              checked={!!fieldChecked[employee.hash_id]?.[field.key]}
              onChange={e => toggleFieldChecked(employee.hash_id, field.key, e.target.checked)}
            />
            <label className="form-check-label ml-1" htmlFor={cbId}>{fieldLabel(field)}</label>
          </div>
          {renderInstanceField(field, values, employee.hash_id)}
        </div>
      );
    };

    const renderGroup = (title, fields) => fields.length > 0 && (
      <div className="mb-2">
        <div className="font-weight-bold mb-2">{title}</div>
        <hr className="mt-2" />
        <div className="row">
          {fields.map(renderFieldCell)}
        </div>
      </div>
    );

    if (isDbModuleActive) {
      return (
        <div className="p-3 bg-light rounded mb-2">
          <StateInstanceForm
            sections={stateSections}
            hashId={employee.hash_id}
            checked={stateChecked}
            setChecked={setStateChecked}
            values={stateValues}
            setValues={setStateValues}
            prepend={stateExtraFields.length > 0 ? stateExtraFields.map(renderFieldCell) : null}
          />
        </div>
      );
    }

    return (
      <div className="p-3 bordered rounded mt-2 mb-2">
        {renderGroup(editableTranslate('batch_update.personal_data', 'Personaldaten'), personaldatenFields)}
        {renderGroup(editableTranslate('batch_update.billing_data', 'Abrechnungsdaten'), abrechnungsdatenFields)}
        {renderGroup(editableTranslate('global.menu.settings.free_fields', 'Freifelder'), freeFields)}
      </div>
    );
  };

  return (
    <div>
      <div className="mb-3">{editableTranslate('action.detail.fill_per_employee_text', 'Wählen Sie Beschäftigte aus und füllen Sie die Felder aus.')}</div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <InputAffix suffix={<i className="bi bi-search ml-2"></i>}>
          <Input data-testid="search-input"
            type="text"
            placeholder={translate('global.search', 'Suche')}
            maxLength={255}
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
          />
        </InputAffix>
        <div className="text-muted text-nowrap ml-2">{checked.length} / {total}</div>
      </div>

      {
        loadingPool && (
          <div className="text-center p-2" data-testid="loading">
            <div className="spinner-border spinner-border-sm" role="status"><span className="sr-only">Wird geladen...</span></div>
          </div>
        )
      }

      {
        employees.length === 0
          ? (!loadingPool && <div className="text-muted p-2" data-testid="empty-state">{editableTranslate('global.no_data', 'Keine Daten')}</div>)
          : employees.map(employee => {
            const isChecked = checked.includes(employee.hash_id);
            return (
              <div key={employee.hash_id} className="border-bottom py-2">
                <div className="form-check">
                  <label className="form-check-label">
                    <input className="form-check-input" type="checkbox" checked={isChecked} onChange={e => toggleRow(employee.hash_id, e.target.checked)} data-testid={`employee-row-select-${employee.hash_id}`} />
                    <span className="ml-1">
                      {`${employee.first_name || ''} ${employee.last_name || ''}`.trim()}
                      {employee.employee_number ? <span className="text-muted ml-1">({employee.employee_number})</span> : null}
                    </span>
                  </label>
                </div>
                {isChecked && renderEmployeeForm(employee)}
              </div>
            );
          })
      }

      <ListPagination
        page={page}
        perPage={perPage}
        totalPages={totalPages}
        totalRows={totalRows}
        setPage={setPage}
        setPerPage={setPerPage}
      />
    </div>
  );
}
